import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, ArrowRight } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const SERVICES = [
  "Framing",
  "Sheetrock & Spackle",
  "Painting",
  "Doors, Windows & Moldings",
  "Bathrooms",
  "Kitchens",
  "Decks",
  "Siding",
  "Other",
];

export default function Contact({ content }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill your name, email and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Thanks! We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send message.");
    } finally {
      setSubmitting(false);
    }
  };

  const tel = content.phone.replace(/[^+\d]/g, "");

  return (
    <section id="contact" className="py-24 md:py-32 bg-neutral-950 text-white" data-testid="contact-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12">
          {/* Left info */}
          <div className="lg:col-span-5">
            <div className="text-sm uppercase tracking-widest text-orange-500 mb-4 font-medium">
              — Get in touch
            </div>
            <h2 className="font-display font-black uppercase leading-[0.9] tracking-tighter text-4xl sm:text-5xl md:text-6xl">
              Free estimate. <br />
              <span className="text-orange-500">Zero pressure.</span>
            </h2>
            <p className="mt-6 text-neutral-300 text-base md:text-lg leading-relaxed max-w-md">
              Tell us about your project. We&apos;ll come out, take measurements, and walk you through a clear, itemized plan.
            </p>

            <div className="mt-10 space-y-5">
              <a
                href={`tel:${tel}`}
                className="flex items-start gap-4 group"
                data-testid="contact-phone-link"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-orange-600 shrink-0">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-neutral-400">Call</div>
                  <div className="font-display font-bold text-xl group-hover:text-orange-400 transition-colors">
                    {content.phone}
                  </div>
                </div>
              </a>

              <a
                href={`mailto:${content.email}`}
                className="flex items-start gap-4 group"
                data-testid="contact-email-link"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-neutral-800 shrink-0 border border-neutral-700">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-neutral-400">Email</div>
                  <div className="font-display font-bold text-lg group-hover:text-orange-400 transition-colors">
                    {content.email}
                  </div>
                </div>
              </a>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-neutral-800 shrink-0 border border-neutral-700">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-neutral-400">Service Area</div>
                  <div className="font-display font-bold text-lg">{content.location}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              className="bg-white text-neutral-950 p-8 md:p-12"
              data-testid="contact-form"
            >
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-neutral-500 mb-2 font-medium">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full border border-neutral-300 focus:border-orange-600 outline-none px-4 py-3 font-body"
                    data-testid="contact-name-input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-neutral-500 mb-2 font-medium">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full border border-neutral-300 focus:border-orange-600 outline-none px-4 py-3 font-body"
                    data-testid="contact-email-input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-neutral-500 mb-2 font-medium">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full border border-neutral-300 focus:border-orange-600 outline-none px-4 py-3 font-body"
                    data-testid="contact-phone-input"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-neutral-500 mb-2 font-medium">
                    Service Needed
                  </label>
                  <select
                    value={form.service}
                    onChange={(e) => setForm({ ...form, service: e.target.value })}
                    className="w-full border border-neutral-300 focus:border-orange-600 outline-none px-4 py-3 font-body bg-white"
                    data-testid="contact-service-input"
                  >
                    <option value="">Select a service…</option>
                    {SERVICES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-5">
                <label className="block text-xs uppercase tracking-widest text-neutral-500 mb-2 font-medium">
                  Tell us about your project *
                </label>
                <textarea
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full border border-neutral-300 focus:border-orange-600 outline-none px-4 py-3 font-body resize-none"
                  data-testid="contact-message-input"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="mt-6 inline-flex items-center gap-3 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white font-display font-bold uppercase tracking-wider px-8 py-4 transition-colors"
                data-testid="contact-submit-btn"
              >
                {submitting ? "Sending…" : (
                  <>
                    <Send className="w-4 h-4" />
                    Send Request
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
